import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Table, Image, Spinner, Accordion, Badge } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import '../App.css';

interface EpisodeScore {
  tribeImmunityWins: number;
  tribeRewardWins: number;
  individualRewardWins: number;
  rightSideOfVote: number;
  individualImmunityWins: number;
  votesAgainst: number;
  idolObtained: number;
  advantageObtained: number;
  playedIdolAdvantage: number;
  advantageAbandoned: number;
  idolAbandoned: number;
  eliminationPick: number;
  shotInDarkWin: number;
  coinFlipWin?: number;
  finalPlacement: number;
  votedOut: boolean;
  involuntaryEvacuation?: boolean;
  voluntaryExit?: boolean;
  notes?: string;
}

interface AllScores {
  [episode: string]: {
    [castawayId: string]: EpisodeScore;
  };
}

const scoringRubric = {
  tribeImmunityWins: 1,
  tribeRewardWins: 1,
  individualRewardWins: 2,
  rightSideOfVote: 1,
  individualImmunityWins: 1, // Multiplier
  votesAgainst: -0.5,
  idolObtained: 4,
  advantageObtained: 2,
  playedIdolAdvantage: 2,
  advantageAbandoned: -2,
  idolAbandoned: -3,
  eliminationPick: 3,
  shotInDarkWin: 4,
  coinFlipWin: 4,
  finalPlacement: { 1: 25, 2: 10, 3: 5 },
};

const calculateCastawayEpisodeScore = (scores: EpisodeScore, episodeNum?: number): number => {
  if (episodeNum === 1) return 0; // Pre-draft episode tracking only
  let episodeTotal = 0;
  if (!scores) return 0;

  episodeTotal += (scores.tribeImmunityWins || 0) * scoringRubric.tribeImmunityWins;
  episodeTotal += (scores.tribeRewardWins || 0) * scoringRubric.tribeRewardWins;
  episodeTotal += (scores.individualRewardWins || 0) * scoringRubric.individualRewardWins;
  episodeTotal += (scores.rightSideOfVote || 0) * scoringRubric.rightSideOfVote;
  episodeTotal += (scores.individualImmunityWins || 0); // Already in points
  episodeTotal += (scores.votesAgainst || 0) * scoringRubric.votesAgainst;
  episodeTotal += (scores.idolObtained || 0) * scoringRubric.idolObtained;
  episodeTotal += (scores.advantageObtained || 0) * scoringRubric.advantageObtained;
  episodeTotal += (scores.playedIdolAdvantage || 0) * scoringRubric.playedIdolAdvantage;
  episodeTotal += (scores.advantageAbandoned || 0) * scoringRubric.advantageAbandoned;
  episodeTotal += (scores.idolAbandoned || 0) * scoringRubric.idolAbandoned;
  episodeTotal += (scores.eliminationPick || 0) * scoringRubric.eliminationPick;
  episodeTotal += (scores.shotInDarkWin || 0) * scoringRubric.shotInDarkWin;
  episodeTotal += (scores.coinFlipWin || 0) * scoringRubric.coinFlipWin;

  if (scores.finalPlacement && scoringRubric.finalPlacement[scores.finalPlacement as keyof typeof scoringRubric.finalPlacement]) {
    const finalPoints = scoringRubric.finalPlacement[scores.finalPlacement as keyof typeof scoringRubric.finalPlacement];
    episodeTotal += finalPoints;
  }
  return episodeTotal;
};

const CastawayDetail: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [tribes, setTribes] = useState<{ name: string; color: string }[]>([]);
  const { leagueId, castawayId } = useParams<{ leagueId: string, castawayId: string }>();
  const [allScores, setAllScores] = useState<AllScores>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    return parseInt(localStorage.getItem('selectedSeason') || '49');
  });

  useEffect(() => {
    const handleSeasonChange = () => {
      setSelectedSeason(parseInt(localStorage.getItem('selectedSeason') || '49'));
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, []);

  useEffect(() => {
    if (leagueId) {
      const loadCastaways = async () => {
        setIsLoading(true);
        try {
          const [castawaysResponse, leaguesResponse] = await Promise.all([
              fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/castaways`),
              fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`)
          ]);
          
          const castawaysData = await castawaysResponse.json();
          const leaguesData = await leaguesResponse.json();
          const currentLeague = leaguesData.leagues?.find((l: any) => l.id === leagueId);

          if (currentLeague) {
            const leagueSeason = currentLeague.season || 49;
            if (leagueSeason != selectedSeason) {
              setError(`Season ${selectedSeason} not yet configured.`);
              setAllCastaways([]);
              return;
            }
            if (currentLeague.tribes) {
              setTribes(currentLeague.tribes);
            }
          }

          setAllCastaways(castawaysData.castaways || []);
        } catch (err) {
          setError('Could not load castaway data.');
        } finally {
          setIsLoading(false);
        }
      };
      loadCastaways();
    }
  }, [leagueId, selectedSeason]);
  
  const getCastawayById = (id: number) => allCastaways.find(c => c.id === id);

  const castaway = getCastawayById(Number(castawayId));

  const finalPlacement = Object.values(allScores).reduce((acc, ep) => {
    const p = ep[castawayId!]?.finalPlacement;
    return (p && p > 0) ? p : acc;
  }, 0);

  useEffect(() => {
    if (castaway) {
      document.title = `${castaway.name} - Score Details`;
    } else {
      document.title = 'Castaway Not Found';
    }
  }, [castaway]);

  useEffect(() => {
    const fetchScores = async () => {
      if (!castawayId) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`);
        if (!response.ok) {
          throw new Error('Failed to fetch scores');
        }
        const data = await response.json();
        setAllScores(data.scores || {});
      } catch (err: any) {
        setError(err.message || 'An error occurred');
        setAllScores({});
      } finally {
        setIsLoading(false);
      }
    };
    fetchScores();
  }, [castawayId, selectedSeason]);

  if (!castaway) {
    return <Container className="mt-5"><Alert variant="danger">Castaway not found.</Alert></Container>;
  }

  const formatCategoryName = (camelCase: string) => {
    return camelCase.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  let grandTotal = 0;
  const episodeNumbers = Object.keys(allScores).map(Number).sort((a, b) => a - b);

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">
          <div style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
            <Image
              src={castaway.imageUrl}
              roundedCircle
              referrerPolicy="no-referrer"
              style={{
                width: '50px',
                height: '50px',
                marginRight: '15px',
                objectFit: 'cover',
                border: `3px solid ${getTribeColor(castaway.tribe, tribes, castaway.tribeColor)}`
              }}
            />
            {finalPlacement > 0 && (
              <Badge 
                bg={finalPlacement === 1 ? 'warning' : finalPlacement === 2 ? 'secondary' : 'danger'} 
                text={finalPlacement === 1 ? 'dark' : 'white'}
                style={{ 
                  position: 'absolute', 
                  top: -5, 
                  left: -5, 
                  fontSize: '0.7rem',
                  border: finalPlacement === 1 ? '1px solid gold' : finalPlacement === 2 ? '1px solid silver' : '1px solid #cd7f32',
                  backgroundColor: finalPlacement === 3 ? '#cd7f32' : undefined
                }}
              >
                {finalPlacement === 1 ? '1st' : finalPlacement === 2 ? '2nd' : '3rd'}
              </Badge>
            )}
          </div>
          <span className="align-middle">{castaway.name}</span>
          <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} className="ms-2 align-middle" />
          <span className="ms-2 align-middle text-muted small">- Score Breakdown</span>
        </Card.Header>
        <Card.Body>
          {isLoading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!isLoading && !error && (
            <>
              <Accordion defaultActiveKey="0">
                {episodeNumbers.map(episodeNum => {
                  const episode = String(episodeNum);
                  const scores = allScores[episode]?.[castawayId!];
                  if (scores) {
                    const isEp1 = episodeNum === 1;
                    const episodeTotal = calculateCastawayEpisodeScore(scores, episodeNum);
                    grandTotal += episodeTotal;
                    return (
                      <Accordion.Item eventKey={episode} key={episode}>
                        <Accordion.Header>
                          Episode {episode} {isEp1 ? '(Pre-Draft Tracking)' : ''} - Total: {episodeTotal} pts
                        </Accordion.Header>
                        <Accordion.Body>
                          {isEp1 && (
                            <Alert variant="info" className="py-2 small">
                              Pre-draft event tracking only. Idol/advantage discoveries and eliminations are tracked for inventory and status, with 0 fantasy points awarded.
                            </Alert>
                          )}
                          {scores.notes && (
                            <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#f0f9ff', fontSize: '0.85rem', border: '1px solid #cce5ff', color: '#495057' }}>
                              <span className="me-1">🤖</span>
                              <strong>Episode Summary:</strong> {scores.notes}
                            </div>
                          )}
                          <Table striped bordered size="sm">
                            <thead><tr><th>Category</th><th>Points</th></tr></thead>
                            <tbody>
                              {Object.entries(scores).map(([category, value]) => {
                                if (value === 0 || category === 'votedOut' || category === 'involuntaryEvacuation' || category === 'voluntaryExit') return null;
                                const numValue = Number(value);
                                if (isNaN(numValue)) return null;
                                let points = 0;
                                if (!isEp1) {
                                  if (category === 'individualImmunityWins') points = numValue;
                                  else if (category === 'votesAgainst') points = numValue * scoringRubric.votesAgainst;
                                  else if (category === 'finalPlacement' && scoringRubric.finalPlacement[numValue as keyof typeof scoringRubric.finalPlacement]) points = scoringRubric.finalPlacement[numValue as keyof typeof scoringRubric.finalPlacement];
                                  else if (scoringRubric[category as keyof typeof scoringRubric]) points = numValue * (scoringRubric[category as keyof Omit<typeof scoringRubric, 'finalPlacement'>] as number);
                                }
                                return (
                                  <tr key={category}>
                                    <td>{formatCategoryName(category)}</td>
                                    <td>{isEp1 ? `${numValue} (0 pts - Pre-Draft)` : points}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                          {scores.votedOut && <Alert variant="danger" className="mt-2">Voted Out</Alert>}
                          {scores.involuntaryEvacuation && <Alert variant="info" className="mt-2">Involuntary Evacuation</Alert>}
                          {scores.voluntaryExit && <Alert variant="warning" className="mt-2" style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', borderColor: '#d8b4fe' }}>Voluntary Exit</Alert>}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  }
                  return null;
                })}
              </Accordion>
              <h5 className="text-end mt-3">Grand Total: {grandTotal}</h5>
            </>
          )}
          <div className="text-center mt-4">
            <Link to={`/league/${leagueId}`} className="btn btn-secondary">Back to League Standings</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CastawayDetail;